/**
 * PrepLinkScreen.tsx
 * Phase 3 — Écran de liaison des préparations au comptoir.
 *
 * Permet à l'agent du comptoir de :
 * 1. Voir toutes les préparations disponibles
 * 2. Sélectionner une préparation
 * 3. La lier à un contrat client
 * 4. Modifier les données si besoin (photos, dommages, km)
 * 5. Faire signer le client
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '../store/useAppStore'
import {
  ArrowLeft, Package, Link as LinkIcon, Loader2,
  Car, Camera, AlertTriangle, Clock, Search,
  ChevronRight, CheckCircle2, RefreshCw, Eye, X, Trash2,
} from 'lucide-react'
import Card   from '../components/ui/Card'
import Button from '../components/ui/Button'
import api    from '../services/api'
import toast  from 'react-hot-toast'

// ── Types ──────────────────────────────────────────────────────────────────
interface PrepItem {
  id:            string
  immat:         string
  type:          string
  vehicle_name?: string
  mileage?:      number
  fuel?:         number
  damages:       any[]
  photo_urls:    Record<string, string>
  agent_name:    string
  notes?:        string
  created_at:    string
  completed_at?: string
  expires_at?:   string
}

function fmtDate(iso?: string): string {
  if (!iso) return '–'
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '–' }
}

function timeLeft(expiresAt?: string): string {
  if (!expiresAt) return ''
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (ms <= 0) return 'Expiré'
  const h = Math.floor(ms / 3_600_000)
  const m = Math.floor((ms % 3_600_000) / 60_000)
  return `${h}h${m.toString().padStart(2, '0')} restantes`
}

// ══════════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════════

export default function PrepLinkScreen() {
  const setStep          = useAppStore(s => s.setStep)
  const contracts        = useAppStore(s => s.contracts) as any[]
  const credentials      = useAppStore(s => s.credentials)
  const initInspection   = useAppStore(s => s.initInspection)
  const setPhoto         = useAppStore(s => s.setPhoto)
  const updateInspection = useAppStore(s => s.updateInspection)

  const [preps, setPreps]             = useState<PrepItem[]>([])
  const [loading, setLoading]         = useState(true)
  const [selectedPrep, setSelectedPrep] = useState<PrepItem | null>(null)
  const [linkStep, setLinkStep]       = useState<'list' | 'select-contract' | 'preview' | 'linking'>('list')
  const [contractSearch, setContractSearch] = useState('')
  const [selectedContract, setSelectedContract] = useState<any>(null)
  const [linking, setLinking]         = useState(false)

  // ── Charger les préparations ───────────────────────────────────────────
  const loadPreps = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/edl/prep/list')
      setPreps(res.data?.preparations || [])
    } catch (err: any) {
      console.error('[PrepLink] Erreur chargement:', err)
      toast.error('Erreur chargement des préparations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadPreps() }, [loadPreps])

  // ── Supprimer une prépa ──────────────────────────────────────────────
  const handleDeletePrep = async (e: React.MouseEvent, prepId: string) => {
    e.stopPropagation()
    if (!window.confirm('Voulez-vous vraiment supprimer cet état préparatoire ?')) return
    
    const toastId = toast.loading('Suppression...')
    try {
      await api.delete(`/api/edl/prep/${prepId}`)
      toast.success('Préparation supprimée', { id: toastId })
      loadPreps()
    } catch (err: any) {
      console.error('[PrepLink] Erreur suppression:', err)
      toast.error('Erreur lors de la suppression', { id: toastId })
    }
  }

  // ── Filtrer les contrats ───────────────────────────────────────────────
  const filteredContracts = contractSearch.length >= 2
    ? contracts.filter((c: any) => {
        const q = contractSearch.toUpperCase()
        return (c.plate || '').toUpperCase().includes(q) ||
               (c.clientName || '').toUpperCase().includes(q)
      }).slice(0, 20)
    : contracts.slice(0, 30)

  // ── Sélectionner une prépa ──────────────────────────────────────────────
  const handleSelectPrep = (prep: PrepItem) => {
    setSelectedPrep(prep)
    setLinkStep('select-contract')
  }

  // ── Sélectionner un contrat ────────────────────────────────────────────
  const handleSelectContract = (contract: any) => {
    setSelectedContract(contract)
    setLinkStep('preview')
  }

  // ── Lier la préparation ────────────────────────────────────────────────
  const handleLink = async () => {
    if (!selectedPrep || !selectedContract) return
    setLinking(true)

    try {
      const contractId = selectedContract.rawId ?? selectedContract.id
      await api.post(`/api/edl/prep/${selectedPrep.id}/link`, {
        contractId: String(contractId),
        agentNameComptoir: (credentials as any)?.username || 'Agent comptoir',
        clientName: selectedContract.clientName,
      })

      toast.success(`Préparation liée à ${selectedContract.plate} ✅`)

      // ── Initialiser l'inspection avec le contrat + type de la prépa ──
      const type = selectedPrep.type as 'depart' | 'retour'
      initInspection(selectedContract, type)

      // ── Charger les photos de la prépa dans photoMemory ──────────────
      const photoEntries = Object.entries(selectedPrep.photo_urls || {})
      if (photoEntries.length > 0) {
        toast.loading(`Chargement des ${photoEntries.length} photos…`, { id: 'prep-photos' })

        await Promise.allSettled(
          photoEntries.map(async ([zone, url]) => {
            try {
              const response = await fetch(url)
              const blob = await response.blob()
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result as string)
                reader.onerror = reject
                reader.readAsDataURL(blob)
              })
              setPhoto(zone, dataUrl, dataUrl)
            } catch (e: any) {
              console.warn(`[PrepLink] Erreur chargement photo ${zone}:`, e?.message)
            }
          })
        )

        toast.dismiss('prep-photos')
      }

      // ── Pré-remplir km, carburant et dommages de la prépa ────────────
      updateInspection({
        damages: selectedPrep.damages || [],
        mileage: selectedPrep.mileage ?? null,
        fuel:    selectedPrep.fuel ?? null,
      })

      // ── Naviguer directement vers les photos pour validation client ──
      setStep('photos')

    } catch (err: any) {
      console.error('[PrepLink] Erreur liaison:', err)
      toast.error(err?.response?.data?.error || 'Erreur lors de la liaison')
    } finally {
      setLinking(false)
    }
  }

  // ── Retour arrière ──────────────────────────────────────────────────────
  const handleBack = () => {
    if (linkStep === 'preview') {
      setLinkStep('select-contract')
    } else if (linkStep === 'select-contract') {
      setSelectedPrep(null)
      setLinkStep('list')
    } else {
      setStep('contracts')
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Rendu
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-4 pt-5 pb-5">
        <button
          onClick={handleBack}
          className="text-white/70 hover:text-white mb-3 flex items-center gap-1 text-sm"
        >
          <ArrowLeft size={16}/> {linkStep === 'list' ? 'Retour' : 'Précédent'}
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Package size={22} className="text-white"/>
          </div>
          <div>
            <h1 className="text-white font-black text-xl">
              {linkStep === 'list' && 'Préparations disponibles'}
              {linkStep === 'select-contract' && 'Choisir un contrat'}
              {linkStep === 'preview' && 'Vérification'}
              {linkStep === 'linking' && 'Liaison en cours...'}
            </h1>
            <p className="text-white/70 text-sm">
              {linkStep === 'list' && `${preps.length} préparation${preps.length > 1 ? 's' : ''}`}
              {linkStep === 'select-contract' && `${selectedPrep?.immat} → ?`}
              {linkStep === 'preview' && `${selectedPrep?.immat} → ${selectedContract?.plate}`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-lg mx-auto w-full">

        {/* ── STEP 1 : Liste des préparations ──────────────────────────────── */}
        {linkStep === 'list' && (
          <div className="flex flex-col gap-3">
            {/* Bouton refresh */}
            <div className="flex justify-end">
              <button
                onClick={loadPreps}
                disabled={loading}
                className="text-xs font-semibold text-purple-600 flex items-center gap-1"
              >
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/>
                Actualiser
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={28} className="animate-spin text-purple-500"/>
              </div>
            ) : preps.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4"/>
                <h2 className="text-lg font-semibold text-gray-600 mb-2">
                  Aucune préparation disponible
                </h2>
                <p className="text-sm text-gray-400">
                  Les préparations faites au garage apparaîtront ici.
                </p>
              </div>
            ) : (
              preps.map(prep => (
                <button
                  key={prep.id}
                  onClick={() => handleSelectPrep(prep)}
                  className="w-full text-left"
                >
                  <Card className="p-4 hover:shadow-md transition-all active:scale-[0.98] border-2 border-transparent hover:border-purple-300">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Car size={24} className="text-purple-600"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-gray-800">{prep.immat}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                            prep.type === 'depart'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {prep.type === 'depart' ? 'DÉPART' : 'RETOUR'}
                          </span>
                        </div>
                        {prep.vehicle_name && (
                          <p className="text-xs text-gray-500 truncate">{prep.vehicle_name}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                          <span>👤 {prep.agent_name}</span>
                          <span>📷 {Object.keys(prep.photo_urls).length}</span>
                          {prep.mileage != null && <span>🛣️ {prep.mileage} km</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px]">
                          <Clock size={10} className="text-gray-400"/>
                          <span className="text-gray-400">{fmtDate(prep.completed_at)}</span>
                          <span className="text-orange-500 font-semibold">{timeLeft(prep.expires_at)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => handleDeletePrep(e, prep.id)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer la préparation"
                        >
                          <Trash2 size={18} />
                        </button>
                        <ChevronRight size={18} className="text-gray-300"/>
                      </div>
                    </div>
                  </Card>
                </button>
              ))
            )}
          </div>
        )}

        {/* ── STEP 2 : Sélection du contrat ──────────────────────────────── */}
        {linkStep === 'select-contract' && selectedPrep && (
          <div className="flex flex-col gap-3">
            {/* Résumé de la prépa */}
            <Card className="p-3 bg-purple-50 border-purple-200">
              <div className="flex items-center gap-3 text-sm">
                <Car size={16} className="text-purple-600"/>
                <span className="font-mono font-bold text-purple-800">{selectedPrep.immat}</span>
                <span className="text-purple-600">→ Choisir le contrat à lier</span>
              </div>
            </Card>

            {/* Recherche */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input
                type="text"
                value={contractSearch}
                onChange={e => setContractSearch(e.target.value)}
                placeholder="Rechercher plaque ou client..."
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:border-purple-400 outline-none"
              />
            </div>

            {/* Liste des contrats */}
            <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
              {filteredContracts.map((contract: any) => (
                <button
                  key={contract.id}
                  onClick={() => handleSelectContract(contract)}
                  className="w-full text-left p-3 bg-white border border-gray-100 rounded-xl hover:border-purple-300 hover:bg-purple-50/30 transition-all active:scale-[0.98]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="font-mono font-bold text-sm text-gray-800">{contract.plate}</span>
                      <p className="text-xs text-gray-500 truncate">{contract.clientName}</p>
                      {contract.vehicleName && (
                        <p className="text-[11px] text-gray-400 truncate">{contract.vehicleName}</p>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-gray-300"/>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 3 : Preview et confirmation ───────────────────────────── */}
        {linkStep === 'preview' && selectedPrep && selectedContract && (
          <div className="flex flex-col gap-4">

            {/* Résumé liaison */}
            <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <h3 className="text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                <LinkIcon size={14}/> Liaison
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">Préparation</p>
                  <p className="font-mono font-bold text-gray-800">{selectedPrep.immat}</p>
                  <p className="text-xs text-gray-500">Agent : {selectedPrep.agent_name}</p>
                  <p className="text-xs text-gray-500">{fmtDate(selectedPrep.completed_at)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">Contrat</p>
                  <p className="font-mono font-bold text-gray-800">{selectedContract.plate}</p>
                  <p className="text-xs text-gray-500">{selectedContract.clientName}</p>
                  {selectedContract.vehicleName && (
                    <p className="text-xs text-gray-400">{selectedContract.vehicleName}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Données de la préparation */}
            <Card className="p-4">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Données de la préparation</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-blue-50 rounded-xl p-2">
                  <p className="text-lg font-black text-blue-600">
                    {Object.keys(selectedPrep.photo_urls).length}
                  </p>
                  <p className="text-[10px] text-gray-500">Photos</p>
                </div>
                <div className="bg-red-50 rounded-xl p-2">
                  <p className="text-lg font-black text-red-600">
                    {selectedPrep.damages?.length || 0}
                  </p>
                  <p className="text-[10px] text-gray-500">Dommages</p>
                </div>
                <div className="bg-green-50 rounded-xl p-2">
                  <p className="text-lg font-black text-green-600">
                    {selectedPrep.mileage ?? '—'}
                  </p>
                  <p className="text-[10px] text-gray-500">km</p>
                </div>
              </div>

              {selectedPrep.notes && (
                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  <p className="text-xs text-yellow-700">📝 {selectedPrep.notes}</p>
                </div>
              )}
            </Card>

            {/* Avertissement plaque différente */}
            {selectedPrep.immat.toUpperCase().replace(/[^A-Z0-9]/g, '') !==
             (selectedContract.plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '') && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5"/>
                <div className="text-sm">
                  <p className="font-bold text-red-700">Plaques différentes !</p>
                  <p className="text-red-600 text-xs">
                    Préparation : {selectedPrep.immat} ≠ Contrat : {selectedContract.plate}
                  </p>
                  <p className="text-red-500 text-xs mt-1">
                    Vérifiez que c'est bien le bon véhicule.
                  </p>
                </div>
              </div>
            )}

            {/* Bouton de liaison */}
            <Button
              onClick={handleLink}
              variant="primary"
              disabled={linking}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              {linking ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin"/>
                  Liaison en cours...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <LinkIcon size={16}/>
                  Lier au contrat {selectedContract.plate}
                </div>
              )}
            </Button>

            <p className="text-center text-[10px] text-gray-400">
              L'EDL sera créé et intégré dans MyRentCar pour le contrat sélectionné.
            </p>
          </div>
        )}

      </div>
    </div>
  )
}
